import { TutorialScrollLockDirective } from '../../../shared/directives/tutorial-scroll-lock.directive';
import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AlertController } from '@ionic/angular';
import { Router, RouterModule } from '@angular/router';
import { UserSectionSidenavComponent } from '../../../shared/components/user-section-sidenav/user-section-sidenav.component';
import { MatListModule } from '@angular/material/list';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { Subscription, tap } from 'rxjs';
import { AuthService } from '../../../core/services/auth.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IUser } from '../../../shared/models/be/user';
import { RequestsService } from '../../../core/services/utils/requests.service';
import { IonContent } from '@ionic/angular/standalone';
import { SharedService } from '../../../core/services/shared.service';
import { environment } from '../../../../environments/environment';
import { positionTutorialCard, TutorialCardPosition } from '../../../shared/utils/tutorial-card-position.util';
import { MonitoringOutletComponent } from '../../../shared/components/monitoring-outlet/monitoring-outlet.component';
import { GuideService } from '../../../core/services/guide.service';
import { GuideTourComponent } from '../../../shared/components/guide-tour/guide-tour.component';

@Component({
  selector: 'app-root',
  templateUrl: './root.page.html',
  styleUrls: ['./root.page.scss'],
  imports: [TutorialScrollLockDirective,
    GuideTourComponent,
    MonitoringOutletComponent,
    CommonModule,
    FormsModule,
    RouterModule,
    UserSectionSidenavComponent,
    MatListModule,
    MatExpansionModule,
    MatIconModule,
    TranslateModule,
    IonContent
  ]
})
export class RootPage implements OnInit, AfterViewInit, OnDestroy {
  @ViewChildren('tutorialNavItem', { read: ElementRef }) tutorialNavItems!: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('tutorialCardElement', { read: ElementRef }) tutorialCardElement?: ElementRef<HTMLElement>;
  isSidenavCollapsed: boolean = false;

  showSubmenu: boolean = false;
  isShowing = false;
  showSubSubMenu: boolean = false;
  public selectedItem: string = '';
  public loggedUser?: IUser;
  public environment = environment;
  public menuItems: IMenuItem[] = [];
  public tutorialMenuItems: IMenuItem[] = [];
  public tutorialActive: boolean = false;
  public tutorialStepIndex: number = 0;
  public tutorialSpotlight?: DesktopTutorialSpotlight;
  public tutorialCardPosition?: TutorialCardPosition;

  private guideSubscription?: Subscription;
  private tutorialSpotlightTimeout?: ReturnType<typeof setTimeout>;
  // Keep the guide focused on operational pages, including for superusers.
  private readonly tutorialMenuItemIds = new Set([
    'mentorage', 'alertsHistory', 'settings', 'analytics', 'guide', 'support', 'logout'
  ]);

  constructor(
    private router: Router,
    private authService: AuthService,
    private requestsService: RequestsService,
    public sharedService: SharedService,
    public guide: GuideService
  ) {
    this.loggedUser = requestsService.getCurrentUser() || undefined;
    requestsService.currentUser$.pipe(
      tap((currentUser: IUser | null) => {
        this.loggedUser = currentUser || undefined;
      })
    ).subscribe();
    requestsService.selectedSidebarItem$.pipe(
      tap((selectedItem) => {
        this.selectedItem = selectedItem || this.selectedItem;
      })
    ).subscribe();
  }

  ngOnInit() {
    this.loggedUser = this.requestsService.getCurrentUser() || undefined;
    this.menuItems = [
      {
        id: 'customers',
        label: 'CUSTOMERS',
        icon: 'group',
        routerLink: '/wita/customers',
        hide: !['admin', 'superuser'].includes(this.loggedUser?.role || "")
      },
      {
        id: 'users',
        label: 'USERS',
        icon: 'groups',
        routerLink: '/wita/users',
        hide: !['admin', 'superuser', 'localadmin'].includes(this.loggedUser?.role || "")
      },
      {
        id: 'structure',
        label: 'STRUCTURE',
        icon: 'domain',
        routerLink: '/wita/structure',
        hide: !['admin', 'superuser'].includes(this.loggedUser?.role || "")
      },
      {
        id: 'devices',
        label: 'SENSORS_LIST',
        icon: 'videocam',
        routerLink: '/wita/devices',
        hide: !['admin', 'superuser'].includes(this.loggedUser?.role || "")
      },
      {
        id: 'mentorage',
        label: 'MENTORAGE',
        icon: 'mentorage',
        routerLink: '/wita/mentorage',
        isSvgIcon: true
      },
      {
        id: 'alertsHistory',
        label: 'ALERTS_HISTORY',
        icon: 'menu_book',
        routerLink: '/wita/alertsHistory'
      },
      {
        id: 'settings',
        label: 'SETTINGS',
        icon: 'settings',
        routerLink: '/wita/settings'
      },
      {
        id: 'analytics',
        label: 'ANALYTICS',
        icon: 'analytics',
        routerLink: '/wita/analytics',
        hide: !['superuser', 'localadmin'].includes(this.loggedUser?.role || "")
      },
      {
        id: 'diagnostic',
        label: 'DIAGNOSTIC',
        icon: 'monitor_heart',
        routerLink: '/wita/diagnostic',
        hide: !['superuser'].includes(this.loggedUser?.role || "")
      },
      {
        id: 'logs',
        label: 'LOGS',
        icon: 'history',
        routerLink: '/wita/logs',
        hide: this.loggedUser?.role !== 'superuser'
      },
      {
        id: 'developerSettings',
        label: 'DEVELOPER_SETTINGS',
        icon: 'code',
        routerLink: '/wita/developerSettings',
        hide: !['superuser'].includes(this.loggedUser?.role || "")
      },
      {
        id: 'support',
        label: 'SUPPORT',
        icon: 'bug_report',
        routerLink: '/wita/support'
      },
      { id: 'guide', label: 'GUIDE', icon: 'help_outline', routerLink: '/wita/guide' },
      {
        id: 'logout',
        label: 'LOGOUT',
        icon: 'logout',
        routerLink: ''
      },
    ].filter((item) => !item.hide);
    this.guide.setAnalyticsAvailable(this.menuItems.some(item => item.id === 'analytics'));
    this.tutorialMenuItems = this.menuItems.filter((item) => this.tutorialMenuItemIds.has(item.id));

    if (!this.selectedItem && this.menuItems.some((item) => this.router.url.includes(item.routerLink!))) {
      this.selectedItem = this.menuItems.find((item) => this.router.url.includes(item.routerLink!))!.id;
    }
  }

  ngAfterViewInit(): void {
    this.guideSubscription = this.guide.request$.subscribe(module => {
      if (module === 'menu' || module === 'openMentorage' || module === 'openHistory' || module === 'openSettings' || module === 'openAnalytics') this.startTutorial();
      else this.closeTutorial();
    });
  }

  @HostListener('window:resize')
  onTutorialViewportChange(): void {
    if (this.tutorialActive) {
      this.scheduleTutorialSpotlightUpdate();
    }
  }

  onTutorialSidebarScroll(): void {
    if (this.tutorialActive) {
      this.updateTutorialPosition();
    }
  }

  get currentTutorialItem(): IMenuItem | undefined {
    if (this.isGuideNavigationStep) return this.menuItems.find(item => item.id === this.guide.navigationMenuItemId);
    return this.tutorialMenuItems[this.tutorialStepIndex];
  }

  get isGuideNavigationStep(): boolean { return this.guide.activeModule === 'openMentorage' || this.guide.activeModule === 'openHistory' || this.guide.activeModule === 'openSettings' || this.guide.activeModule === 'openAnalytics'; }

  onMenuItemClick(event: Event, item: IMenuItem): void {
    if (this.isGuideNavigationStep) {
      event.preventDefault();
      if (item.id === this.guide.navigationMenuItemId) void this.guide.enterRequestedPage();
      return;
    }
    if (item.id === 'logout') this.logout();
  }

  get tutorialPrimaryButtonKey(): string {
    if (this.tutorialStepIndex < this.tutorialMenuItems.length - 1) {
      return 'DESKTOP_TUTORIAL.BUTTONS.NEXT';
    }
    return !this.guide.hasNextModule
      ? 'GUIDE.CONCLUDE_TUTORIAL' : 'DESKTOP_TUTORIAL.BUTTONS.FINISH';
  }

  nextTutorialStep(): void {
    if (this.isGuideNavigationStep) return;
    if (this.tutorialStepIndex >= this.tutorialMenuItems.length - 1) {
      this.finishTutorial();
      return;
    }

    this.tutorialStepIndex += 1;
    this.focusCurrentTutorialItem();
  }

  previousTutorialStep(): void {
    if (this.tutorialStepIndex === 0) {
      return;
    }

    this.tutorialStepIndex -= 1;
    this.focusCurrentTutorialItem();
  }

  skipTutorial(): void {
    this.closeTutorial();
    void this.guide.cancel();
  }

  ngOnDestroy(): void {
    this.guideSubscription?.unsubscribe();
    if (this.tutorialSpotlightTimeout) {
      clearTimeout(this.tutorialSpotlightTimeout);
    }
  }

  private startTutorial(): void {
    if ((this.guide.activeModule !== 'menu' && !this.isGuideNavigationStep) || !this.tutorialMenuItems.length) return;
    this.isSidenavCollapsed = false;
    this.tutorialStepIndex = 0;
    this.tutorialActive = true;
    this.focusCurrentTutorialItem();
  }

  private finishTutorial(): void {
    this.closeTutorial();
    void this.guide.completeModule();
  }

  private focusCurrentTutorialItem(): void {
    setTimeout(() => {
      if (!this.tutorialActive) {
        return;
      }
      this.getCurrentTutorialTarget()?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
      this.scheduleTutorialSpotlightUpdate(350, true);
    });
  }

  private scheduleTutorialSpotlightUpdate(delay: number = 80, resetPosition: boolean = false): void {
    if (this.tutorialSpotlightTimeout) {
      clearTimeout(this.tutorialSpotlightTimeout);
    }

    if (resetPosition) {
      this.tutorialSpotlight = undefined;
      this.tutorialCardPosition = undefined;
    }
    this.tutorialSpotlightTimeout = setTimeout(() => this.updateTutorialPosition(), delay);
  }

  private updateTutorialPosition(): void {
    const target = this.getCurrentTutorialTarget();
    const card = this.tutorialCardElement?.nativeElement;
    if (!target || !card) {
      return;
    }

    const rect = target.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const sidebarRect = target.closest('.sidenav-panel')?.getBoundingClientRect() ?? rect;
    // Keep both the border and its 5px halo inside the sidebar.
    const left = Math.max(0, sidebarRect.left) + 5;
    const right = Math.min(window.innerWidth, sidebarRect.right) - 5;
    this.tutorialSpotlight = {
      top: Math.max(5, rect.top - 4),
      left,
      width: Math.max(0, right - left),
      height: rect.height + 8
    };
    this.tutorialCardPosition = positionTutorialCard(rect, cardRect, {
      width: window.innerWidth,
      height: window.innerHeight
    }, 20);
  }

  private getCurrentTutorialTarget(): HTMLElement | undefined {
    const menuIndex = this.menuItems.findIndex((item) => item.id === this.currentTutorialItem?.id);
    return menuIndex < 0 ? undefined : this.tutorialNavItems.get(menuIndex)?.nativeElement;
  }

  private closeTutorial(): void {
    this.tutorialActive = false;
    this.tutorialSpotlight = undefined;
    this.tutorialCardPosition = undefined;
    if (this.tutorialSpotlightTimeout) clearTimeout(this.tutorialSpotlightTimeout);
  }

  logout() {
    this.authService.logout$().subscribe();
  }


  toggleSidenavCollapsed() {
    this.isSidenavCollapsed = !this.isSidenavCollapsed;
  }
}

interface IMenuItem {
  /**
   * Identificativo della voce di menu
   */
  id: string;
  /**
   * Label della voce di menu
   */
  label: string;
  /**
   * Icona, N.B. Deve essere di Angular Material l'icona
   */
  icon: string;
  /**
   * Route della pagina in cui si vuole navigare, opzionale nel caso siano presenti figli
   */
  routerLink?: string;
  /**
   * Se true richiede la presenza di una organizzazione, qualora non presente comparirà un Popup
   */
  requireOrganization?: boolean;
  /**
   * Opzionale, se true non viene mostrata la voce di menu
   */
  hide?: boolean;
  isSvgIcon?: boolean;
}

interface DesktopTutorialSpotlight {
  top: number;
  left: number;
  width: number;
  height: number;
}
